// src/store/reducers/serviceReducer.js
import { SERVICE_ACTIONS } from '../actions/actionTypes';

const initialState = {
  services: [],
  selectedService: null,
  loading: false,
  error: null,
  success: null,
  meta: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  },
  filters: {
    page: 1,
    limit: 20,
    search: '',
    isActive: true,
    sortBy: 'name',
    sortOrder: 'asc',
  },
  serviceAgencies: [],
  agenciesMeta: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  },
  popularServices: [],
  stats: null,
};

const serviceReducer = (state = initialState, action) => {
  switch (action.type) {
    // Fetch services
    case SERVICE_ACTIONS.FETCH_SERVICES_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case SERVICE_ACTIONS.FETCH_SERVICES_SUCCESS:
      return {
        ...state,
        loading: false,
        services: action.payload.data || [],
        meta: action.payload.meta || initialState.meta,
        error: null,
      };

    case SERVICE_ACTIONS.FETCH_SERVICES_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        services: [],
      };

    // Fetch single service
    case SERVICE_ACTIONS.FETCH_SERVICE_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case SERVICE_ACTIONS.FETCH_SERVICE_SUCCESS:
      return {
        ...state,
        loading: false,
        selectedService: action.payload.data || action.payload,
        error: null,
      };

    case SERVICE_ACTIONS.FETCH_SERVICE_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        selectedService: null,
      };

    // Create service
    case SERVICE_ACTIONS.CREATE_SERVICE_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        success: null,
      };

    case SERVICE_ACTIONS.CREATE_SERVICE_SUCCESS:
      return {
        ...state,
        loading: false,
        services: [action.payload, ...state.services],
        success: 'Service créé avec succès',
        error: null,
      };

    case SERVICE_ACTIONS.CREATE_SERVICE_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        success: null,
      };

    // Update service
    case SERVICE_ACTIONS.UPDATE_SERVICE_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        success: null,
      };

    case SERVICE_ACTIONS.UPDATE_SERVICE_SUCCESS:
      return {
        ...state,
        loading: false,
        services: state.services.map(service =>
          service.id === action.payload.id ? action.payload : service
        ),
        selectedService: action.payload,
        success: 'Service mis à jour avec succès',
        error: null,
      };

    case SERVICE_ACTIONS.UPDATE_SERVICE_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        success: null,
      };

    // Delete service
    case SERVICE_ACTIONS.DELETE_SERVICE_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        success: null,
      };

    case SERVICE_ACTIONS.DELETE_SERVICE_SUCCESS:
      return {
        ...state,
        loading: false,
        services: state.services.filter(service => service.id !== action.payload.id),
        success: action.payload.message || 'Service supprimé avec succès',
        error: null,
      };

    case SERVICE_ACTIONS.DELETE_SERVICE_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        success: null,
      };

    // Toggle service status
    case SERVICE_ACTIONS.TOGGLE_SERVICE_STATUS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case SERVICE_ACTIONS.TOGGLE_SERVICE_STATUS_SUCCESS:
      return {
        ...state,
        loading: false,
        services: state.services.map(service =>
          service.id === action.payload.id 
            ? { ...service, isActive: action.payload.isActive }
            : service
        ),
        selectedService: state.selectedService?.id === action.payload.id
          ? { ...state.selectedService, isActive: action.payload.isActive }
          : state.selectedService,
        success: `Service ${action.payload.isActive ? 'activé' : 'désactivé'} avec succès`,
        error: null,
      };

    case SERVICE_ACTIONS.TOGGLE_SERVICE_STATUS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Fetch service agencies
    case SERVICE_ACTIONS.FETCH_SERVICE_AGENCIES_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case SERVICE_ACTIONS.FETCH_SERVICE_AGENCIES_SUCCESS:
      return {
        ...state,
        loading: false,
        serviceAgencies: action.payload.data || [],
        agenciesMeta: action.payload.meta || initialState.agenciesMeta,
        error: null,
      };

    case SERVICE_ACTIONS.FETCH_SERVICE_AGENCIES_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        serviceAgencies: [],
      };

    // Fetch popular services
    case SERVICE_ACTIONS.FETCH_POPULAR_SERVICES_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case SERVICE_ACTIONS.FETCH_POPULAR_SERVICES_SUCCESS:
      return {
        ...state,
        loading: false,
        popularServices: action.payload,
        error: null,
      };

    case SERVICE_ACTIONS.FETCH_POPULAR_SERVICES_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        popularServices: [],
      };

    // Fetch service statistics
    case SERVICE_ACTIONS.FETCH_SERVICE_STATS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case SERVICE_ACTIONS.FETCH_SERVICE_STATS_SUCCESS:
      return {
        ...state,
        loading: false,
        stats: action.payload,
        error: null,
      };

    case SERVICE_ACTIONS.FETCH_SERVICE_STATS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        stats: null,
      };

    // Bulk delete
    case SERVICE_ACTIONS.BULK_DELETE_SERVICES_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case SERVICE_ACTIONS.BULK_DELETE_SERVICES_SUCCESS:
      return {
        ...state,
        loading: false,
        services: state.services.filter(service => !action.payload.ids.includes(service.id)),
        success: action.payload.message || 'Services supprimés avec succès',
        error: null,
      };

    case SERVICE_ACTIONS.BULK_DELETE_SERVICES_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Set filters
    case SERVICE_ACTIONS.SET_SERVICE_FILTERS:
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.payload,
        },
      };

    // Clear states
    case SERVICE_ACTIONS.CLEAR_SERVICE_ERROR:
      return {
        ...state,
        error: null,
      };

    case SERVICE_ACTIONS.CLEAR_SERVICE_SUCCESS:
      return {
        ...state,
        success: null,
      };

    case SERVICE_ACTIONS.CLEAR_SELECTED_SERVICE:
      return {
        ...state,
        selectedService: null,
        serviceAgencies: [],
      };

    default:
      return state;
  }
};

export default serviceReducer;